import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfDay,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useAuth } from '../../context/AuthProvider';
import {
  SCHEDULE_MEETING_TYPE_COLORS,
  SCHEDULE_MEETING_TYPE_LABELS,
  type ScheduleMeetingType,
} from '../../lib/schedule/meetingTypes';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import {
  useBookableScheduleSlots,
  useCreateScheduleEvent,
  useCreateScheduleSlots,
  useDeleteScheduleSlot,
  useDeleteRule,
  useCreateRule,
  useRules,
  useScheduleEvents,
  useScheduleMeetingTypes,
  useScheduleSettings,
  useScheduleSlots,
  useUpdateScheduleSettings,
  type ScheduleEvent,
  type ScheduleSlot,
} from '../../hooks/useSchedule';
import { useApi } from '../../context/ApiProvider';
import { useUsers } from '../../hooks/useUsers';
import BaseCard from '../../components/Card';
import PortalAvatar from '../../components/profile/PortalAvatar';

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEKDAY_SHORT_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const OVERVIEW_AVAILABILITY_EXCLUDED_TYPES: readonly ScheduleMeetingType[] = [
  'interview',
  'vacation',
  'out_of_office',
];
type DrawerUserOption = {
  uid: string;
  email: string;
  name: string | null;
  businessName: string | null;
  role: string;
  photoURL: string | null;
  initials: string;
  label: string;
  search: string;
};
const AUTO_AVAIL_RULE_PREFIX = 'AUTO_AVAIL::';
const scheduleAccent = '#e0a15e';

const meetingDotPulse = keyframes`
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(224, 161, 94, 0.38);
    opacity: 0.88;
  }
  70% {
    transform: scale(1.14);
    box-shadow: 0 0 0 8px rgba(224, 161, 94, 0);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(224, 161, 94, 0);
    opacity: 0.88;
  }
`;

type DayAvailabilitySettings = {
  enabled: boolean;
  startTime: string;
  endTime: string;
};

type SettingsByDay = Record<number, DayAvailabilitySettings>;

function isValidIanaTimezone(timezone: string) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function getInitials(value: string): string {
  const tokens = value
    .split(/[\s@._-]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (!tokens.length) return '?';
  const first = tokens[0]?.[0] ?? '';
  const second = tokens[1]?.[0] ?? '';
  const initials = `${first}${second}`.toUpperCase();
  return initials.length ? initials : '?';
}

type DayDrawerRepeatMode = 'none' | 'weekly' | 'monthly' | 'daily';
type DayDrawerTabId = 'event' | 'availability';
type AvailabilityWindowSummary = {
  startTime: string;
  endTime: string;
  hasRecurring: boolean;
  hasOneOff: boolean;
};

function expandDatesWithRecurrence(
  baseDates: string[],
  repeatMode: DayDrawerRepeatMode,
  repeatForRange: boolean,
  repeatRangeEnd: string,
): string[] {
  const uniqueSortedBaseDates = Array.from(new Set(baseDates)).sort((a, b) =>
    a.localeCompare(b),
  );
  if (!uniqueSortedBaseDates.length) return [];
  if (repeatMode === 'none' || !repeatForRange || !repeatRangeEnd.trim().length) {
    return uniqueSortedBaseDates;
  }

  const endDate = parseISO(`${repeatRangeEnd}T00:00:00`);
  if (Number.isNaN(endDate.getTime())) return uniqueSortedBaseDates;
  const expandedDates = new Set(uniqueSortedBaseDates);

  for (const baseDate of uniqueSortedBaseDates) {
    let cursor = parseISO(`${baseDate}T00:00:00`);
    if (Number.isNaN(cursor.getTime())) continue;

    while (true) {
      if (repeatMode === 'weekly') {
        cursor = addDays(cursor, 7);
      } else if (repeatMode === 'monthly') {
        cursor = addMonths(cursor, 1);
      } else {
        cursor = addDays(cursor, 1);
      }
      if (cursor.getTime() > endDate.getTime()) break;
      expandedDates.add(format(cursor, 'yyyy-MM-dd'));
    }
  }

  return Array.from(expandedDates).sort((a, b) => a.localeCompare(b));
}

function getDefaultRepeatRangeEnd(
  firstDate: string,
  repeatMode: Exclude<DayDrawerRepeatMode, 'none'>,
): string {
  const parsed = parseISO(`${firstDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return firstDate;
  if (repeatMode === 'weekly') return format(addDays(parsed, 28), 'yyyy-MM-dd');
  if (repeatMode === 'monthly') return format(addMonths(parsed, 3), 'yyyy-MM-dd');
  return format(addDays(parsed, 14), 'yyyy-MM-dd');
}

const Page = styled.section`
  max-width: 1320px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(1.5)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Header = styled.div`
  display: grid;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${scheduleAccent};
  font-size: clamp(1.7rem, 3.5vw, 2.4rem);
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
`;

const Layout = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OperatorLayout = styled(Layout)`
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
`;

const OperatorTop = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  // border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme }) => theme.colors.gray050};
  border-radius: 16px;
  // padding: ${({ theme }) => theme.spacing(1)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.9)};
`;

const PanelTitle = styled.h3`
  margin: 0;
  color: ${scheduleAccent};
  font-size: 1rem;
`;

const Text = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Helper = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.82rem;
  opacity: 0.86;
`;

const Button = styled.button<{ $danger?: boolean; $ghost?: boolean }>`
  border-radius: 10px;
  min-height: 38px;
  padding: 0 12px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme, $danger, $ghost }) =>
    $ghost
      ? 'transparent'
      : $danger
      ? 'rgba(166, 76, 68, 0.2)'
      : theme.colors.primary};
  color: ${({ theme, $ghost }) => ($ghost ? theme.colors.gray700 : '#fff')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 800;
  color: ${scheduleAccent};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  min-height: 38px;
  padding: 0 10px;
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.gray700};
`;

const Textarea = styled.textarea`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  min-height: 80px;
  padding: 10px;
  resize: vertical;
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.gray700};
  font-family: inherit;
`;

const EventsRailPanel = styled(Panel)`
  min-height: 620px;
  background: transparent;
  align-content: start;
  color: #1a1a1a;
`;

const EventsRailWrap = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.8)};
  align-content: start;
`;

const EventsRailHeader = styled.div`
  display: grid;
  gap: 4px;
`;

const EventsRailHeading = styled.h3`
  margin: 0;
  color: ${scheduleAccent};
  font-size: clamp(1.7rem, 3.5vw, 2.4rem);
  font-weight: 700;
`;

const EventsRailSubtext = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.95rem;
  font-weight: 700;
`;

const EventsRailListShell = styled.div`
  // border: 2px solid rgba(26, 26, 26, 0.7);

  border-radius: 12px;
  padding: 8px;
  height: 530px;
  overflow: hidden;
`;

const EventsRailList = styled.div`
  display: grid;
  gap: 0.55rem;
  height: 100%;
  align-content: start;
  overflow: auto;
  padding-right: 4px;
`;

const EventsRailCard = styled.article`
  border-radius: 12px;
  border: 2px solid rgba(26, 26, 26, 0.72);
  background: #969696;
  padding: 10px;
  min-height: 88px;
  display: grid;
  align-content: start;
  gap: 4px;
`;

const EventsRailTitle = styled.div`
  margin: 0;
  color: #a64c44;
  font-size: 0.86rem;
  font-weight: 900;
`;

const EventsRailMeta = styled.div`
  margin: 0;
  color: rgba(26, 26, 26, 0.88);
  font-size: 0.78rem;
  font-weight: 800;
`;

const CalendarPanel = styled(Panel)`
  min-height: 620px;
  background: #e0a15e;
  border: 1px solid rgba(26, 26, 26, 0.6);
  padding: 0.75rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
`;

const CalendarNavGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`;

const CalendarSettingsButton = styled.button`
  border: 2px solid rgba(26, 26, 26, 0.55);
  border-radius: 10px;
  min-height: 34px;
  padding: 0 10px;
  background: rgba(26, 26, 26, 0.12);
  color: #1a1a1a;
  font-size: 0.76rem;
  font-weight: 900;
  cursor: pointer;
`;

const MonthLabel = styled.h3`
  margin: 0;
  color: #1a1a1a;
  font-size: 1.1rem;
  font-weight: 900;
`;

const NavButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 2px solid rgba(26, 26, 26, 0.55);
  background: rgba(26, 26, 26, 0.08);
  color: #1a1a1a;
  cursor: pointer;
  font-weight: 900;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.45rem;
`;

const DayHeader = styled.div`
  text-align: center;
  font-size: 0.72rem;
  font-weight: 900;
  color: #1a1a1a;
  opacity: 0.9;
`;

const DayCell = styled.button<{
  $out: boolean;
  $disabled: boolean;
  $today: boolean;
  $futureInMonth: boolean;
  $selected: boolean;
}>`
  border: ${({ $out, $today, $futureInMonth, $selected }) => {
    if ($out) return '1px solid rgba(26, 26, 26, 0.34)';
    if ($today) return '4px solid #a64c44';
    if ($selected) return '4px solid #1a1a1a';
    if ($futureInMonth) return '3px solid #1a1a1a';
    return '1px solid rgba(26, 26, 26, 0.4)';
  }};
  border-radius: 10px;
  min-height: 76px;
  padding: 6px;
  background: ${({ $selected }) =>
    $selected ? 'rgba(26, 26, 26, 0.08)' : 'transparent'};
  color: #1a1a1a;
  display: grid;
  align-content: space-between;
  justify-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  opacity: ${({ $out, $disabled }) => {
    if ($out) return 0.86;
    if ($disabled) return 0.55;
    return 1;
  }};
`;

const DayNumber = styled.div<{
  $out: boolean;
  $today: boolean;
  $futureInMonth: boolean;
}>`
  color: ${({ $out, $today }) => {
    if ($out) return 'rgba(26, 26, 26, 0.74)';
    if ($today) return '#a64c44';
    return '#1a1a1a';
  }};
  font-weight: ${({ $out, $today, $futureInMonth }) => {
    if ($today || $futureInMonth) return 900;
    if ($out) return 600;
    return 650;
  }};
`;

const DotRow = styled.div`
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Dot = styled.span<{ $type: ScheduleMeetingType }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${({ $type }) => SCHEDULE_MEETING_TYPE_COLORS[$type]};
  animation: ${meetingDotPulse} 1.8s ease-in-out infinite;
  transform-origin: center;
`;

const Item = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.gray100};
  padding: 8px 10px;
  display: grid;
  gap: 3px;
`;

const ItemTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 800;
  color: ${scheduleAccent};
`;

const ItemMeta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.gray700};
`;

const Row = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SlotGroup = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const SlotDateLabel = styled.div`
  color: ${scheduleAccent};
  font-size: 0.84rem;
  font-weight: 800;
`;

const SlotButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const SlotButton = styled.button<{ $active: boolean; $type: ScheduleMeetingType }>`
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  min-height: 30px;
  padding: 0 10px;
  font-size: 0.76rem;
  font-weight: 800;
  cursor: pointer;
  background: ${({ $active, $type, theme }) =>
    $active ? SCHEDULE_MEETING_TYPE_COLORS[$type] : theme.colors.gray100};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.gray700)};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.gray200};
`;

const dayDrawerFlatField = css`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #e8eaed;
  outline: none;
  box-shadow: none;
  font-weight: 700;

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    background: rgba(255, 255, 255, 0.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
    outline: 2px solid rgba(138, 180, 248, 0.35);
    outline-offset: 1px;
  }
`;

const DayDrawerBackdrop = styled(motion.button)`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2100;
`;

const DayDrawerSheet = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  height: 100dvh;
  @supports not (height: 100dvh) {
    height: 100vh;
  }
  width: clamp(420px, 72vw, 820px);
  z-index: 2101;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.45);
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  overflow: hidden;
  color-scheme: dark;

  @media (max-width: 860px) {
    width: 100vw;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const DayDrawerHeader = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const DayDrawerTitleWrap = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  min-width: 0;
  padding-right: 96px;
`;

const DayDrawerTitle = styled.h3`
  margin: 0;
  color: #e8eaed;
  font-size: 0.98rem;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DayDrawerDate = styled.p`
  margin: 0;
  color: #9aa4b2;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.45;
`;

const DayDrawerClose = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
`;

const DayDrawerTabs = styled.nav`
  display: flex;
  gap: 2px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const DayDrawerTab = styled.button<{ $active: boolean }>`
  min-height: 38px;
  margin-bottom: -1px;
  border-radius: 12px 12px 0 0;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)'};
  border-bottom-color: transparent;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  color: ${({ $active }) => ($active ? '#f8ebd6' : '#cbd2e1')};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
  padding: 0 14px;
  cursor: pointer;
`;

const DayDrawerBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 14px 12px 18px;
  display: grid;
  gap: 18px;
  align-content: start;
  align-items: start;
`;

const DayDrawerSection = styled.section`
  display: grid;
  gap: 10px;
  align-content: start;
  align-self: start;

  & + & {
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const DayDrawerSectionTitle = styled.h4`
  margin: 0;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DayDrawerTypeSelect = styled.select`
  ${dayDrawerFlatField}
  min-height: 40px;
  padding: 7px 38px 7px 10px;
  font-size: 13px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23cbd2e1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;

  option {
    background: #12161b;
    color: #e8eaed;
  }
`;

const DayDrawerMeetingTypePicker = styled.div`
  position: relative;
`;

const DayDrawerMeetingTypeButton = styled.button<{ $open: boolean }>`
  width: 100%;
  ${dayDrawerFlatField}
  min-height: 40px;
  padding: 7px 10px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  box-shadow: ${({ $open }) =>
    $open ? 'inset 0 0 0 1px rgba(138, 180, 248, 0.75)' : 'none'};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const DayDrawerMeetingTypeButtonLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DayDrawerMeetingTypeCaret = styled.span<{ $open: boolean }>`
  font-size: 0.74rem;
  line-height: 1;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.16s ease;
`;

const DayDrawerMeetingTypeMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(18, 20, 25, 0.98);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  z-index: 40;
  padding: 6px 0;
`;

const DayDrawerMeetingTypeOption = styled.button<{ $selected: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  padding: 10px 12px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? 'rgba(255, 255, 255, 0.06)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#f8ebd6' : '#e8eaed')};
  font-size: 12px;
  font-weight: 800;

  &:hover,
  &:focus-visible,
  &:active {
    background: rgba(255, 255, 255, 0.06);
    color: #f3f4f6;
  }
`;

const DayDrawerTypeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  align-items: start;
`;

const DayDrawerUsersRow = styled.div`
  display: grid;
  gap: 8px;
`;

const DayDrawerUsersTitle = styled.h5`
  margin: 0;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DayDrawerUserPicker = styled.div`
  position: relative;
`;

const DayDrawerSelectedUsersList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DayDrawerSelectedUserAvatar = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const DayDrawerSelectedUserRemove = styled.button`
  all: unset;
  box-sizing: border-box;
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(18, 20, 25, 0.98);
  color: #e8eaed;
  font-size: 0.62rem;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
`;

const DayDrawerAddUserButton = styled.button<{ $active: boolean }>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) =>
    $active ? 'rgba(255, 255, 255, 0.24)' : 'rgba(255, 255, 255, 0.12)'};
  background: rgba(255, 255, 255, 0.03);
  color: #cbd2e1;
  font-size: 1rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DayDrawerUserMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(340px, 82vw);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(18, 20, 25, 0.98);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  z-index: 30;
  padding: 8px;
  display: grid;
  gap: 8px;
`;

const DayDrawerUserSearch = styled.input`
  ${dayDrawerFlatField}
  min-height: 34px;
  padding: 0 10px;
  font-size: 12px;

  &::placeholder {
    color: #9aa4b2;
  }
`;

const DayDrawerUserList = styled.div`
  display: grid;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
`;

const DayDrawerUserItem = styled.button<{ $selected: boolean }>`
  all: unset;
  cursor: pointer;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) =>
    $selected ? 'rgba(224, 161, 94, 0.32)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${({ $selected }) =>
    $selected ? 'rgba(224, 161, 94, 0.08)' : 'rgba(255, 255, 255, 0.03)'};
  padding: 8px 10px;
  display: grid;
  gap: 2px;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const DayDrawerUserName = styled.span`
  color: #e8eaed;
  font-size: 0.8rem;
  font-weight: 800;
`;

const DayDrawerUserMeta = styled.span`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const DayDrawerUserEmpty = styled.span`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  padding: 12px 0;
`;

const DayDrawerField = styled.label`
  display: grid;
  gap: 6px;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DayDrawerInput = styled.input`
  ${dayDrawerFlatField}
  min-height: 40px;
  padding: 8px 12px;
  font-size: 13px;
`;

const DayDrawerTextarea = styled.textarea`
  ${dayDrawerFlatField}
  min-height: 88px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
  font-family: inherit;
`;

const DayDrawerInlineGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const DayDrawerSectionBox = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
`;

const DayDrawerEventList = styled.div`
  display: grid;
  gap: 8px;
`;

const DayDrawerEventCard = styled.div`
  display: grid;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const DayDrawerEventTitle = styled.div`
  color: #e8eaed;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
`;

const DayDrawerEventMeta = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const DayDrawerAvailabilityCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const DayDrawerAvailabilityCopy = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const DayDrawerAvailabilityTitle = styled.div`
  color: #e8eaed;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
`;

const DayDrawerAvailabilityMeta = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const DayDrawerAvailabilityActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DayDrawerInlineAction = styled.button<{ $danger?: boolean }>`
  min-height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $danger }) =>
      $danger ? 'rgba(248, 113, 113, 0.24)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${({ $danger }) =>
    $danger ? 'rgba(248, 113, 113, 0.08)' : 'rgba(255, 255, 255, 0.03)'};
  color: ${({ $danger }) => ($danger ? '#fecaca' : '#e8eaed')};
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DayDrawerEmptyState = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  padding: 18px 0;
`;

const DayDrawerSelectedDatesWrap = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
`;

const DayDrawerSelectedDatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 6px;
  max-height: 128px;
  overflow-y: auto;
`;

const DayDrawerSelectedDateChip = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #cbd2e1;
  font-size: 0.72rem;
  font-weight: 800;
`;

const SettingsBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(7, 10, 14, 0.72);
  z-index: 2090;
`;

const SettingsModal = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 2091;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const SettingsCard = styled(BaseCard)`
  width: min(700px, 100%);
  max-height: min(88vh, 960px);
  overflow: hidden;
  padding: 0;
  color: #f3f4f6;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
`;

const SettingsHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SettingsTitleWrap = styled.div`
  display: grid;
  gap: 6px;
`;

const SettingsTitle = styled.h3`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 900;
  color: #f3f4f6;
`;

const SettingsSubtitle = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.5;
  color: ${({ $error }) => ($error ? '#fca5a5' : '#9aa4b2')};
`;

const SettingsBody = styled.div`
  padding: 16px;
  overflow-y: auto;
  display: grid;
  gap: 12px;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(224, 161, 94, 0.42) rgba(255, 255, 255, 0.05);

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      180deg,
      rgba(224, 161, 94, 0.58),
      rgba(224, 161, 94, 0.34)
    );
    border-radius: 999px;
    border: 2px solid rgba(17, 20, 27, 0.92);
  }
`;

const SettingsSection = styled(BaseCard)`
  box-shadow: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  display: grid;
  gap: 10px;
`;

const SettingsSectionTitle = styled.h4`
  margin: 0;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa4b2;
  font-weight: 900;
`;

const SettingsDayRows = styled.div`
  display: grid;
  gap: 8px;
`;

const SettingsDayRow = styled.div<{ $enabled: boolean }>`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: ${({ $enabled }) =>
    $enabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.015)'};
  padding: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: 54px minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: end;
  opacity: ${({ $enabled }) => ($enabled ? 1 : 0.84)};

  @media (max-width: 720px) {
    grid-template-columns: 54px minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const SettingsDayLabel = styled.div`
  min-height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  color: #f3f4f6;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
`;

const SettingsField = styled.label`
  display: grid;
  gap: 6px;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #9aa4b2;
  font-weight: 900;
`;

const SettingsSelect = styled.select`
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  min-height: 40px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.03);
  color: #f3f4f6;
  font-size: 0.82rem;
  font-weight: 800;

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, 0.7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, 0.14);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SettingsToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.83rem;
  color: #f3f4f6;
  font-weight: 800;
`;

const SettingsToggleCopy = styled.div`
  display: grid;
  gap: 2px;
`;

const SettingsToggleLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 900;
  color: #f3f4f6;
`;

const SettingsToggleHint = styled.span`
  font-size: 0.74rem;
  font-weight: 700;
  color: #9aa4b2;
`;

const SettingsSwitch = styled.button<{ $active: boolean }>`
  all: unset;
  cursor: pointer;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.45)' : 'rgba(255, 255, 255, 0.14)'};
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.16)' : 'rgba(255, 255, 255, 0.04)'};
  position: relative;
  flex-shrink: 0;
`;

const SettingsSwitchThumb = styled.span<{ $active: boolean }>`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#e0a15e' : 'rgba(243, 244, 246, 0.72)')};
  transform: translateX(${({ $active }) => ($active ? '22px' : '0')});
  transition: transform 140ms ease, background 140ms ease;
`;

const SettingsDayToggleWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 720px) {
    grid-column: 1 / -1;
  }
`;

const SettingsDayToggleText = styled.span`
  font-size: 0.74rem;
  font-weight: 900;
  color: #9aa4b2;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SettingsFooter = styled.div`
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const SettingsCloseButton = styled.button`
  width: 36px;
  min-width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  color: #e8eaed;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.45);
    box-shadow: 0 0 0 1px rgba(224, 161, 94, 0.2);
  }
`;

const SettingsFooterButton = styled.button<{ $accent?: boolean }>`
  min-height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $accent }) =>
      $accent ? 'rgba(224, 161, 94, 0.45)' : 'rgba(255, 255, 255, 0.14)'};
  background: ${({ $accent }) =>
    $accent ? 'rgba(224, 161, 94, 0.14)' : 'rgba(255, 255, 255, 0.04)'};
  color: #e8eaed;
  font-size: 0.75rem;
  font-weight: 800;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.42 : 1)};

  &:disabled {
    cursor: not-allowed;
  }
`;

const DayDrawerScheduleHint = styled.p`
  margin: 0;
  color: #8fa2bf;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
`;

const DayDrawerRepeatList = styled.div`
  display: grid;
  gap: 8px;
`;

const DayDrawerRepeatRow = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DayDrawerRepeatCopy = styled.div`
  display: grid;
  gap: 2px;
`;

const DayDrawerRepeatLabel = styled.span`
  color: #e8eaed;
  font-size: 12px;
  font-weight: 800;
`;

const DayDrawerRepeatHint = styled.span`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const DayDrawerRangeField = styled.label`
  display: grid;
  gap: 6px;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DayDrawerRangeInput = styled.input`
  ${dayDrawerFlatField}
  min-height: 40px;
  padding: 8px 12px;
  font-size: 13px;
`;

const DayDrawerActionBar = styled.div`
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const DayDrawerButton = styled.button<{ $primary?: boolean; $block?: boolean }>`
  min-height: 36px;
  padding: 0 14px;
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  border-radius: ${({ $primary }) => ($primary ? '999px' : '10px')};
  border: 1px solid
    ${({ $primary }) =>
      $primary ? 'rgba(224, 161, 94, 0.38)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${({ $primary }) =>
    $primary
      ? 'rgba(224, 161, 94, 0.14)'
      : 'rgba(255, 255, 255, 0.04)'};
  color: ${({ $primary }) => ($primary ? '#f8ebd6' : '#e8eaed')};
  font-size: ${({ $primary }) => ($primary ? '0.74rem' : '0.8rem')};
  font-weight: 900;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  const intervalMinutes = 15;

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const period = hour < 12 ? 'AM' : 'PM';
      const h12 = hour % 12 || 12;
      slots.push(`${h12}:${minute.toString().padStart(2, '0')} ${period}`);
    }
  }

  return slots;
}

function getTimeRangeSlots(allSlots: string[], startTime: string, endTime: string) {
  const startIndex = allSlots.indexOf(startTime);
  const endIndex = allSlots.indexOf(endTime);
  if (startIndex < 0 || endIndex < 0 || endIndex <= startIndex) return [];
  return allSlots.slice(startIndex, endIndex);
}

function summarizeAvailabilityWindows(
  slots: ScheduleSlot[],
  allSlots: string[],
  getTimeLabel: (slot: ScheduleSlot) => string,
): AvailabilityWindowSummary[] {
  const slotMetaByIndex = new Map<
    number,
    { hasRecurring: boolean; hasOneOff: boolean }
  >();

  for (const slot of slots) {
    const timeLabel = getTimeLabel(slot);
    const slotIndex = allSlots.indexOf(timeLabel);
    if (slotIndex < 0) continue;
    const previous = slotMetaByIndex.get(slotIndex) ?? {
      hasRecurring: false,
      hasOneOff: false,
    };
    slotMetaByIndex.set(slotIndex, {
      hasRecurring: previous.hasRecurring || slot.isRecurring,
      hasOneOff: previous.hasOneOff || !slot.isRecurring,
    });
  }

  const sortedIndices = Array.from(slotMetaByIndex.keys()).sort((a, b) => a - b);
  if (!sortedIndices.length) return [];

  const windows: AvailabilityWindowSummary[] = [];
  let rangeStart = sortedIndices[0];
  let previousIndex = sortedIndices[0];
  let hasRecurring = slotMetaByIndex.get(rangeStart)?.hasRecurring ?? false;
  let hasOneOff = slotMetaByIndex.get(rangeStart)?.hasOneOff ?? false;

  for (let index = 1; index < sortedIndices.length; index++) {
    const currentIndex = sortedIndices[index];
    const currentMeta = slotMetaByIndex.get(currentIndex);
    if (currentIndex === previousIndex + 1) {
      hasRecurring = hasRecurring || Boolean(currentMeta?.hasRecurring);
      hasOneOff = hasOneOff || Boolean(currentMeta?.hasOneOff);
      previousIndex = currentIndex;
      continue;
    }

    windows.push({
      startTime: allSlots[rangeStart] ?? '',
      endTime: allSlots[previousIndex + 1] ?? '12:00 AM',
      hasRecurring,
      hasOneOff,
    });
    rangeStart = currentIndex;
    previousIndex = currentIndex;
    hasRecurring = Boolean(currentMeta?.hasRecurring);
    hasOneOff = Boolean(currentMeta?.hasOneOff);
  }

  windows.push({
    startTime: allSlots[rangeStart] ?? '',
    endTime: allSlots[previousIndex + 1] ?? '12:00 AM',
    hasRecurring,
    hasOneOff,
  });

  return windows;
}

function createDefaultSettingsByDay(
  startTime = '9:00 AM',
  endTime = '5:00 PM',
): SettingsByDay {
  return DAY_HEADERS.reduce((acc, _, dayIndex) => {
    acc[dayIndex] = {
      enabled: dayIndex >= 1 && dayIndex <= 5,
      startTime,
      endTime,
    };
    return acc;
  }, {} as SettingsByDay);
}

function buildCalendarDays(month: Date) {
  const first = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
}

export default function SchedulePage() {
  const api = useApi();
  const { role } = useAuth();
  const isOperator = role === 'admin' || role === 'agent';
  const isClient = role === 'client';
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const allTimeSlots = useMemo(() => generateTimeSlots(), []);

  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);
  const [dayDrawerDate, setDayDrawerDate] = useState<Date | null>(null);
  const [dayDrawerDates, setDayDrawerDates] = useState<string[]>([]);
  const [dayDrawerTab, setDayDrawerTab] = useState<DayDrawerTabId>('event');
  const [dayDrawerShowCreateForm, setDayDrawerShowCreateForm] = useState(false);
  const [dayDrawerType, setDayDrawerType] = useState<ScheduleMeetingType | null>(null);
  const [dayDrawerTypeMenuOpen, setDayDrawerTypeMenuOpen] = useState(false);
  const [dayDrawerAvailabilityType, setDayDrawerAvailabilityType] = useState<ScheduleMeetingType | null>(null);
  const [dayDrawerAvailabilityTime, setDayDrawerAvailabilityTime] = useState('9:00 AM');
  const [dayDrawerStartTime, setDayDrawerStartTime] = useState('9:00 AM');
  const [dayDrawerDurationMinutes, setDayDrawerDurationMinutes] = useState(30);
  const [dayDrawerEventTitle, setDayDrawerEventTitle] = useState('');
  const [dayDrawerEventDescription, setDayDrawerEventDescription] = useState('');

  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [attendees, setAttendees] = useState<Array<{ name: string; email: string }>>(
    [],
  );
  const [dayDrawerUserPickerOpen, setDayDrawerUserPickerOpen] = useState(false);
  const [dayDrawerUserSearch, setDayDrawerUserSearch] = useState('');
  const [dayDrawerSelectedUserUids, setDayDrawerSelectedUserUids] = useState<string[]>([]);
  const [dayDrawerUserPhotoByUid, setDayDrawerUserPhotoByUid] = useState<Record<string, string>>(
    {},
  );
  const [dayDrawerRepeatWeekly, setDayDrawerRepeatWeekly] = useState(false);
  const [dayDrawerRepeatMonthly, setDayDrawerRepeatMonthly] = useState(false);
  const [dayDrawerRepeatDaily, setDayDrawerRepeatDaily] = useState(false);
  const [dayDrawerRepeatForRange, setDayDrawerRepeatForRange] = useState(false);
  const [dayDrawerRepeatRangeEnd, setDayDrawerRepeatRangeEnd] = useState('');
  const dayDrawerTypeMenuRef = useRef<HTMLDivElement | null>(null);
  const dayDrawerUserPickerRef = useRef<HTMLDivElement | null>(null);
  const [calendarSelection, setCalendarSelection] = useState<string[]>([]);
  const [isDaySelectionDragging, setIsDaySelectionDragging] = useState(false);
  const [daySelectionMode, setDaySelectionMode] = useState<'add' | 'remove'>('add');
  const calendarSelectionRef = useRef<string[]>([]);
  const isDaySelectionDraggingRef = useRef(false);
  const dragVisitedDayKeysRef = useRef<Set<string>>(new Set());
  const timezoneSyncAttemptRef = useRef<string | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsByDay, setSettingsByDay] = useState<SettingsByDay>(() =>
    createDefaultSettingsByDay(),
  );
  const [settingsSuspendCalendar, setSettingsSuspendCalendar] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const startDate = format(startOfMonth(month), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(month), 'yyyy-MM-dd');

  const usersQuery = useUsers({
    q: dayDrawerUserSearch.trim(),
    role: 'all',
    page: 0,
    enabled: isOperator,
  });
  const rulesQuery = useRules(isOperator);
  const meetingTypesQuery = useScheduleMeetingTypes(isOperator);
  const settingsQuery = useScheduleSettings(isOperator);
  const slotsQuery = useScheduleSlots({ startDate, endDate }, isOperator);
  const bookableQuery = useBookableScheduleSlots({ startDate, endDate }, isClient);
  const eventsQuery = useScheduleEvents(isOperator || isClient);

  const createSlots = useCreateScheduleSlots();
  const deleteSlot = useDeleteScheduleSlot();
  const createRule = useCreateRule();
  const deleteRule = useDeleteRule();
  const updateScheduleSettings = useUpdateScheduleSettings();
  const syncScheduleTimezone = useUpdateScheduleSettings();
  const createEvent = useCreateScheduleEvent();

  const viewerTimezone = useMemo(() => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof detected === 'string' && detected.trim().length && isValidIanaTimezone(detected)) {
      return detected;
    }
    return 'UTC';
  }, []);
  const timezone = viewerTimezone;
  const formatUtcInViewerTimezone = useCallback(
    (utcIso: string, pattern: string) => {
      const zoned = toZonedTime(parseISO(utcIso), viewerTimezone);
      return format(zoned, pattern);
    },
    [viewerTimezone],
  );
  const operatorSlots = useMemo(() => slotsQuery.data ?? [], [slotsQuery.data]);
  const allowedTypes = ((meetingTypesQuery.data?.allowed ?? []) as ScheduleMeetingType[]).filter(
    Boolean,
  );
  const availabilityTypes = useMemo(
    () =>
      allowedTypes.filter(
        (type) => !OVERVIEW_AVAILABILITY_EXCLUDED_TYPES.includes(type),
      ),
    [allowedTypes],
  );
  const bookableSlots = useMemo(
    () => bookableQuery.data ?? [],
    [bookableQuery.data],
  );

  const slotsByDay = useMemo(() => {
    const map = new Map<string, ScheduleSlot[]>();
    for (const slot of operatorSlots) {
      if (!slot.isActive) continue;
      const viewerDate = formatUtcInViewerTimezone(slot.utcDateTime, 'yyyy-MM-dd');
      const list = map.get(viewerDate) ?? [];
      list.push(slot);
      map.set(viewerDate, list);
    }
    for (const [dayKey, slots] of map.entries()) {
      map.set(
        dayKey,
        [...slots].sort(
          (a, b) =>
            new Date(a.utcDateTime).getTime() - new Date(b.utcDateTime).getTime(),
        ),
      );
    }
    return map;
  }, [formatUtcInViewerTimezone, operatorSlots]);

  const groupedBookable = useMemo(() => {
    const map = new Map<string, ScheduleSlot[]>();
    for (const slot of bookableSlots) {
      if (!slot.isActive) continue;
      const viewerDate = formatUtcInViewerTimezone(slot.utcDateTime, 'yyyy-MM-dd');
      const list = map.get(viewerDate) ?? [];
      list.push(slot);
      map.set(viewerDate, list);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, slots]) => ({
        date,
        slots: slots.sort(
          (a, b) =>
            new Date(a.utcDateTime).getTime() - new Date(b.utcDateTime).getTime(),
        ),
      }));
  }, [bookableSlots, formatUtcInViewerTimezone]);

  const scheduledEventTypesByDay = useMemo(() => {
    const map = new Map<string, ScheduleMeetingType[]>();
    for (const event of eventsQuery.data ?? []) {
      if (event.status !== 'scheduled') continue;
      const viewerDate = formatUtcInViewerTimezone(event.startsAt, 'yyyy-MM-dd');
      const types = map.get(viewerDate) ?? [];
      types.push(event.meetingType);
      map.set(viewerDate, types);
    }
    for (const [dayKey, types] of map.entries()) {
      map.set(dayKey, Array.from(new Set(types)));
    }
    return map;
  }, [eventsQuery.data, formatUtcInViewerTimezone]);

  const selectedSlot = useMemo(
    () => bookableSlots.find((slot) => slot.id === selectedSlotId) ?? null,
    [bookableSlots, selectedSlotId],
  );
  const bookedSlotIds = useMemo(
    () =>
      new Set(
        (eventsQuery.data ?? [])
          .filter((event) => event.status === 'scheduled')
          .map((event) => event.slotId)
          .filter((slotId): slotId is string => typeof slotId === 'string' && slotId.length > 0),
      ),
    [eventsQuery.data],
  );

  const upcomingEvents = useMemo(() => {
    const now = Date.now();
    return (eventsQuery.data ?? [])
      .filter((event) => event.status === 'scheduled')
      .filter((event) => new Date(event.startsAt).getTime() >= now)
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }, [eventsQuery.data]);
  const todaysEvents = useMemo(() => {
    const today = format(toZonedTime(new Date(), viewerTimezone), 'yyyy-MM-dd');
    return (eventsQuery.data ?? [])
      .filter((event) => event.status === 'scheduled')
      .filter(
        (event) =>
          format(toZonedTime(parseISO(event.startsAt), viewerTimezone), 'yyyy-MM-dd') ===
          today,
      )
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }, [eventsQuery.data, viewerTimezone]);
  const operatorUsers = useMemo<DrawerUserOption[]>(() => {
    return (usersQuery.data ?? [])
      .map((user) => {
        const businessName =
          typeof user.businessName === 'string' && user.businessName.trim().length
            ? user.businessName.trim()
            : null;
        const name =
          typeof user.name === 'string' && user.name.trim().length
            ? user.name.trim()
            : null;
        const rawPhotoURL = (user as { photoURL?: unknown }).photoURL;
        const photoURL =
          typeof rawPhotoURL === 'string' && rawPhotoURL.trim().length
            ? rawPhotoURL.trim()
            : null;
        const label = businessName ?? name ?? user.email;
        const initials = getInitials(name ?? businessName ?? user.email);
        const search = `${businessName ?? ''} ${name ?? ''} ${user.email}`.toLowerCase();
        return {
          uid: user.id,
          email: user.email,
          name,
          businessName,
          role: user.role,
          photoURL,
          initials,
          label,
          search,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [usersQuery.data]);
  const operatorUsersByUid = useMemo(
    () => new Map(operatorUsers.map((user) => [user.uid, user])),
    [operatorUsers],
  );
  const filteredOperatorUsers = useMemo(() => {
    const query = dayDrawerUserSearch.trim().toLowerCase();
    if (!query.length) return operatorUsers;
    return operatorUsers.filter((user) => user.search.includes(query));
  }, [dayDrawerUserSearch, operatorUsers]);
  const selectedOperatorUsers = useMemo(
    () =>
      dayDrawerSelectedUserUids
        .map((uid) => operatorUsersByUid.get(uid))
        .filter((user): user is DrawerUserOption => Boolean(user)),
    [dayDrawerSelectedUserUids, operatorUsersByUid],
  );
  const dayDrawerBaseDates = useMemo(() => {
    if (dayDrawerDates.length) return [...dayDrawerDates].sort((a, b) => a.localeCompare(b));
    if (dayDrawerDate) return [format(dayDrawerDate, 'yyyy-MM-dd')];
    return [];
  }, [dayDrawerDate, dayDrawerDates]);
  const dayDrawerFirstDate = dayDrawerBaseDates[0] ?? '';
  const dayDrawerRepeatMode: DayDrawerRepeatMode = dayDrawerRepeatWeekly
    ? 'weekly'
    : dayDrawerRepeatMonthly
    ? 'monthly'
    : dayDrawerRepeatDaily
    ? 'daily'
    : 'none';
  const dayDrawerExpandedDates = useMemo(
    () =>
      expandDatesWithRecurrence(
        dayDrawerBaseDates,
        dayDrawerRepeatMode,
        dayDrawerRepeatForRange,
        dayDrawerRepeatRangeEnd,
      ),
    [
      dayDrawerBaseDates,
      dayDrawerRepeatForRange,
      dayDrawerRepeatMode,
      dayDrawerRepeatRangeEnd,
    ],
  );
  const dayDrawerEventDateKeys = useMemo(
    () => new Set(dayDrawerBaseDates),
    [dayDrawerBaseDates],
  );
  const dayDrawerAvailabilitySlotsByDate = useMemo(
    () =>
      dayDrawerBaseDates.map((dateKey) => ({
        dateKey,
        slots: (slotsByDay.get(dateKey) ?? []).filter((slot) =>
          availabilityTypes.includes(slot.type),
        ),
      })),
    [availabilityTypes, dayDrawerBaseDates, slotsByDay],
  );
  const dayDrawerAvailabilityWindowsByDate = useMemo(
    () =>
      dayDrawerAvailabilitySlotsByDate.map(({ dateKey, slots }) => ({
        dateKey,
        windows: summarizeAvailabilityWindows(
          slots,
          allTimeSlots,
          (slot) => formatUtcInViewerTimezone(slot.utcDateTime, 'h:mm a'),
        ),
      })),
    [allTimeSlots, dayDrawerAvailabilitySlotsByDate, formatUtcInViewerTimezone],
  );
  const dayDrawerCanManageAvailability = dayDrawerBaseDates.length === 1;
  const dayDrawerScheduledEvents = useMemo<
    Array<ScheduleEvent & { dateKey: string }>
  >(
    () =>
      (eventsQuery.data ?? [])
        .filter((event) => event.status === 'scheduled')
        .map((event) => ({
          ...event,
          dateKey: formatUtcInViewerTimezone(event.startsAt, 'yyyy-MM-dd'),
        }))
        .filter((event) => dayDrawerEventDateKeys.has(event.dateKey))
        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()),
    [dayDrawerEventDateKeys, eventsQuery.data, formatUtcInViewerTimezone],
  );
  const dayDrawerRepeatWeekdayLabel = useMemo(() => {
    if (!dayDrawerFirstDate) return 'Repeat every week (same weekday)';
    const parsed = parseISO(`${dayDrawerFirstDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return 'Repeat every week (same weekday)';
    return `Repeat every ${format(parsed, 'EEEE')}`;
  }, [dayDrawerFirstDate]);
  const dayDrawerRepeatRangeInvalid = useMemo(() => {
    if (!dayDrawerRepeatForRange || dayDrawerRepeatMode === 'none') return false;
    if (!dayDrawerRepeatRangeEnd.trim().length || !dayDrawerFirstDate.length) return true;
    const start = parseISO(`${dayDrawerFirstDate}T00:00:00`);
    const end = parseISO(`${dayDrawerRepeatRangeEnd}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;
    return end.getTime() < start.getTime();
  }, [
    dayDrawerFirstDate,
    dayDrawerRepeatForRange,
    dayDrawerRepeatMode,
    dayDrawerRepeatRangeEnd,
  ]);
  const applyDayDrawerRepeatMode = useCallback(
    (mode: Exclude<DayDrawerRepeatMode, 'none'>) => {
      const nextEnabled = dayDrawerRepeatMode !== mode;
      setDayDrawerRepeatWeekly(nextEnabled && mode === 'weekly');
      setDayDrawerRepeatMonthly(nextEnabled && mode === 'monthly');
      setDayDrawerRepeatDaily(nextEnabled && mode === 'daily');
      if (!nextEnabled) return;
      setDayDrawerRepeatForRange(true);
      if (!dayDrawerFirstDate.length) return;
      const firstDateParsed = parseISO(`${dayDrawerFirstDate}T00:00:00`);
      if (Number.isNaN(firstDateParsed.getTime())) return;
      setDayDrawerRepeatRangeEnd((previous) => {
        const previousParsed =
          previous.trim().length > 0 ? parseISO(`${previous}T00:00:00`) : null;
        if (
          previousParsed &&
          !Number.isNaN(previousParsed.getTime()) &&
          previousParsed.getTime() >= firstDateParsed.getTime()
        ) {
          return previous;
        }
        return getDefaultRepeatRangeEnd(dayDrawerFirstDate, mode);
      });
    },
    [dayDrawerFirstDate, dayDrawerRepeatMode],
  );
  const toggleDayDrawerRepeatRange = useCallback(() => {
    setDayDrawerRepeatForRange((current) => {
      const next = !current;
      if (!next) {
        setDayDrawerRepeatWeekly(false);
        setDayDrawerRepeatMonthly(false);
        setDayDrawerRepeatDaily(false);
      } else if (
        dayDrawerRepeatMode !== 'none' &&
        dayDrawerFirstDate.length &&
        !dayDrawerRepeatRangeEnd.trim().length
      ) {
        setDayDrawerRepeatRangeEnd(
          getDefaultRepeatRangeEnd(
            dayDrawerFirstDate,
            dayDrawerRepeatMode as Exclude<DayDrawerRepeatMode, 'none'>,
          ),
        );
      }
      return next;
    });
  }, [dayDrawerFirstDate, dayDrawerRepeatMode, dayDrawerRepeatRangeEnd]);
  useEffect(() => {
    if (!dayDrawerOpen) return;
    const missingUsers = selectedOperatorUsers.filter(
      (user) =>
        !user.photoURL &&
        !(
          typeof dayDrawerUserPhotoByUid[user.uid] === 'string' &&
          dayDrawerUserPhotoByUid[user.uid].trim().length
        ),
    );
    if (!missingUsers.length) return;

    let cancelled = false;
    void Promise.all(
      missingUsers.map(async (user) => {
        try {
          const details = await api.get(`/admin/users/${user.uid}`).then((response) => response.data);
          const nextPhotoURL =
            typeof details?.photoURL === 'string' && details.photoURL.trim().length
              ? details.photoURL.trim()
              : '';
          return [user.uid, nextPhotoURL] as const;
        } catch {
          return [user.uid, ''] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setDayDrawerUserPhotoByUid((previous) => {
        const next = { ...previous };
        let changed = false;
        for (const [uid, photoURL] of entries) {
          if (!photoURL || next[uid] === photoURL) continue;
          next[uid] = photoURL;
          changed = true;
        }
        return changed ? next : previous;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [api, dayDrawerOpen, dayDrawerUserPhotoByUid, selectedOperatorUsers]);
  const autoAvailabilityRules = useMemo(
    () =>
      (rulesQuery.data ?? []).filter((rule) =>
        rule.name.startsWith(AUTO_AVAIL_RULE_PREFIX),
      ),
    [rulesQuery.data],
  );
  const settingsDayRows = useMemo(
    () =>
      WEEKDAY_SHORT_LABELS.map((label, dayIndex) => ({
        label,
        dayIndex,
        config: settingsByDay[dayIndex] ?? {
          enabled: false,
          startTime: '9:00 AM',
          endTime: '5:00 PM',
        },
      })),
    [settingsByDay],
  );
  const settingsDayWindows = useMemo(
    () =>
      settingsDayRows
        .map(({ dayIndex, config }) => ({
          dayIndex,
          enabled: config.enabled,
          times: getTimeRangeSlots(allTimeSlots, config.startTime, config.endTime),
        }))
        .filter((entry) => entry.enabled),
    [allTimeSlots, settingsDayRows],
  );
  const settingsHasInvalidDays = useMemo(
    () => settingsDayWindows.some((entry) => entry.times.length === 0),
    [settingsDayWindows],
  );
  const settingsCanSave =
    settingsSuspendCalendar ||
    (settingsDayWindows.some((entry) => entry.times.length > 0) && !settingsHasInvalidDays);
  const savedScheduleTimezone = settingsQuery.data?.timezone?.trim() ?? '';
  const hasScheduleSettings = Boolean(settingsQuery.data);

  const setCalendarSelectionState = useCallback(
    (updater: (previous: string[]) => string[]) => {
      setCalendarSelection((previous) => {
        const next = updater(previous);
        calendarSelectionRef.current = next;
        return next;
      });
    },
    [],
  );

  const openDayEditorForSelection = useCallback(
    (selectedLocalDates: string[]) => {
      const uniqueSorted = Array.from(new Set(selectedLocalDates)).sort((a, b) =>
        a.localeCompare(b),
      );
      if (!uniqueSorted.length) return;

      const initialType = allowedTypes[0] ?? null;
      const initialAvailabilityType = availabilityTypes[0] ?? null;
      const firstDate = uniqueSorted[0];
      setDayDrawerDates(uniqueSorted);
      setDayDrawerDate(parseISO(`${firstDate}T00:00:00`));
      setDayDrawerType(initialType);
      setDayDrawerAvailabilityType(initialAvailabilityType);
      setDayDrawerAvailabilityTime('9:00 AM');
      setDayDrawerTypeMenuOpen(false);
      setDayDrawerStartTime('9:00 AM');
      setDayDrawerDurationMinutes(30);
      setDayDrawerEventTitle('');
      setDayDrawerEventDescription('');
      setDayDrawerUserPickerOpen(false);
      setDayDrawerUserSearch('');
      setDayDrawerSelectedUserUids([]);
      setDayDrawerRepeatWeekly(false);
      setDayDrawerRepeatMonthly(false);
      setDayDrawerRepeatDaily(false);
      setDayDrawerRepeatForRange(false);
      setDayDrawerRepeatRangeEnd(firstDate);
      setDayDrawerTab('event');
      setDayDrawerShowCreateForm(false);
      setDayDrawerOpen(true);
    },
    [allowedTypes, availabilityTypes],
  );

  const applyDaySelection = useCallback(
    (localDate: string, mode: 'add' | 'remove') => {
      setCalendarSelectionState((previous) => {
        if (mode === 'add') {
          if (previous.includes(localDate)) return previous;
          return [...previous, localDate].sort((a, b) => a.localeCompare(b));
        }
        return previous.filter((value) => value !== localDate);
      });
    },
    [setCalendarSelectionState],
  );

  const startDaySelection = useCallback(
    (date: Date) => {
      const localDate = format(date, 'yyyy-MM-dd');
      const mode: 'add' | 'remove' = calendarSelectionRef.current.includes(localDate)
        ? 'remove'
        : 'add';
      dragVisitedDayKeysRef.current = new Set([localDate]);
      isDaySelectionDraggingRef.current = true;
      setDaySelectionMode(mode);
      setIsDaySelectionDragging(true);
      applyDaySelection(localDate, mode);
    },
    [applyDaySelection],
  );

  const continueDaySelection = useCallback(
    (date: Date) => {
      if (!isDaySelectionDragging) return;
      const localDate = format(date, 'yyyy-MM-dd');
      if (dragVisitedDayKeysRef.current.has(localDate)) return;
      dragVisitedDayKeysRef.current.add(localDate);
      applyDaySelection(localDate, daySelectionMode);
    },
    [applyDaySelection, daySelectionMode, isDaySelectionDragging],
  );

  const finishDaySelection = useCallback(() => {
    if (!isDaySelectionDraggingRef.current) return;
    isDaySelectionDraggingRef.current = false;
    setIsDaySelectionDragging(false);
    dragVisitedDayKeysRef.current.clear();
    const selected = calendarSelectionRef.current;
    if (!selected.length) return;
    openDayEditorForSelection(selected);
  }, [openDayEditorForSelection]);

  useEffect(() => {
    if (!isOperator) return;
    if (!savedScheduleTimezone && !hasScheduleSettings) return;
    if (syncScheduleTimezone.isPending) return;
    const syncKey = `${savedScheduleTimezone}=>${viewerTimezone}`;
    if (timezoneSyncAttemptRef.current === syncKey) return;

    const shouldSync =
      !savedScheduleTimezone ||
      !isValidIanaTimezone(savedScheduleTimezone) ||
      (savedScheduleTimezone === 'UTC' && viewerTimezone !== 'UTC');
    if (!shouldSync || savedScheduleTimezone === viewerTimezone) return;

    timezoneSyncAttemptRef.current = syncKey;
    syncScheduleTimezone.mutate({
      timezone: viewerTimezone,
    });
  }, [
    isOperator,
    hasScheduleSettings,
    savedScheduleTimezone,
    syncScheduleTimezone,
    viewerTimezone,
  ]);

  const closeDayDrawer = () => {
    setDayDrawerOpen(false);
    setDayDrawerDate(null);
    setDayDrawerDates([]);
    setDayDrawerTab('event');
    setDayDrawerShowCreateForm(false);
    setDayDrawerType(null);
    setDayDrawerAvailabilityType(null);
    setDayDrawerAvailabilityTime('9:00 AM');
    setDayDrawerTypeMenuOpen(false);
    setDayDrawerStartTime('9:00 AM');
    setDayDrawerDurationMinutes(30);
    setDayDrawerEventTitle('');
    setDayDrawerEventDescription('');
    setDayDrawerUserPickerOpen(false);
    setDayDrawerUserSearch('');
    setDayDrawerSelectedUserUids([]);
    setDayDrawerRepeatWeekly(false);
    setDayDrawerRepeatMonthly(false);
    setDayDrawerRepeatDaily(false);
    setDayDrawerRepeatForRange(false);
    setDayDrawerRepeatRangeEnd('');
    setCalendarSelection([]);
    calendarSelectionRef.current = [];
    isDaySelectionDraggingRef.current = false;
    setIsDaySelectionDragging(false);
    dragVisitedDayKeysRef.current.clear();
  };

  const updateSettingsDay = (
    dayIndex: number,
    updates: Partial<DayAvailabilitySettings>,
  ) => {
    setSettingsByDay((prev) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        ...updates,
      },
    }));
  };

  useEffect(() => {
    if (!settingsModalOpen) return;
    const sourceRules = autoAvailabilityRules.length ? autoAvailabilityRules : rulesQuery.data ?? [];
    const timesByDay = new Map<number, string[]>();
    for (const rule of sourceRules) {
      for (const day of rule.daysOfWeek) {
        const existing = timesByDay.get(day) ?? [];
        timesByDay.set(day, existing.concat(rule.times));
      }
    }
    const nextSettings = createDefaultSettingsByDay();
    if (timesByDay.size) {
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        nextSettings[dayIndex] = {
          ...nextSettings[dayIndex],
          enabled: false,
        };
      }
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayTimes = timesByDay.get(dayIndex) ?? [];
        const sortedTimes = Array.from(new Set(dayTimes))
          .filter((value) => allTimeSlots.includes(value))
          .sort((a, b) => allTimeSlots.indexOf(a) - allTimeSlots.indexOf(b));
        if (!sortedTimes.length) continue;
        const startTime = sortedTimes[0];
        const endIndex = allTimeSlots.indexOf(sortedTimes[sortedTimes.length - 1]) + 1;
        const endTime = allTimeSlots[endIndex] ?? '11:45 PM';
        nextSettings[dayIndex] = {
          enabled: true,
          startTime,
          endTime,
        };
      }
    }
    setSettingsByDay(nextSettings);
    setSettingsSuspendCalendar(Boolean(settingsQuery.data?.isSuspended));
  }, [
    settingsModalOpen,
    autoAvailabilityRules,
    rulesQuery.data,
    allTimeSlots,
    settingsQuery.data?.isSuspended,
  ]);

  const handleSaveAvailabilitySettings = async () => {
    if (!settingsSuspendCalendar && (!settingsCanSave || settingsHasInvalidDays)) return;
    const validDayWindows = settingsDayWindows.filter((entry) => entry.times.length > 0);
    setSettingsSaving(true);
    try {
      await updateScheduleSettings.mutateAsync({
        isSuspended: settingsSuspendCalendar,
        timezone,
      });
      const oldAutoRuleIds = autoAvailabilityRules
        .map((rule) => rule.id)
        .filter((ruleId): ruleId is string => typeof ruleId === 'string' && ruleId.length > 0);
      await Promise.all(oldAutoRuleIds.map((ruleId) => deleteRule.mutateAsync(ruleId)));

      if (!settingsSuspendCalendar && availabilityTypes.length && validDayWindows.length) {
        await Promise.all(
          validDayWindows.flatMap((entry) =>
            availabilityTypes.map((type) =>
              createRule.mutateAsync({
                name: `${AUTO_AVAIL_RULE_PREFIX}${type}::${entry.dayIndex}`,
                startDate: format(new Date(), 'yyyy-MM-dd'),
                times: entry.times,
                daysOfWeek: [entry.dayIndex],
                pattern: 'weekly',
                type,
                timezone,
              }),
            ),
          ),
        );
      }
      setSettingsModalOpen(false);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCreateEventFromDayDrawer = async () => {
    if (!dayDrawerType || !dayDrawerStartTime) return;
    if (dayDrawerRepeatRangeInvalid) return;
    const selectedDates = dayDrawerExpandedDates;
    if (!selectedDates.length) return;
    try {
      const createdSlots = await createSlots.mutateAsync(
        selectedDates.map((localDate) => ({
          localDate,
          localTime: dayDrawerStartTime,
          timezone,
          type: dayDrawerType,
          isActive: true,
          isRecurring: false,
        })),
      );
      const slotIdByDate = new Map<string, string>();
      for (const slot of createdSlots?.slots ?? []) {
        const viewerDate = formatUtcInViewerTimezone(slot.utcDateTime, 'yyyy-MM-dd');
        slotIdByDate.set(viewerDate, slot.id);
      }
      const selectedUsersOrdered = dayDrawerSelectedUserUids
        .map((uid) => operatorUsersByUid.get(uid))
        .filter((user): user is DrawerUserOption => Boolean(user));
      const primaryClientUser = selectedUsersOrdered.find((user) => user.role === 'client') ?? null;
      const additionalAttendees = selectedUsersOrdered
        .filter((user) => user.uid !== primaryClientUser?.uid)
        .map((user) => ({
          userId: user.uid,
          email: user.email,
          name: user.name ?? user.businessName ?? user.label,
          role: user.role,
        }));

      for (const localDate of selectedDates) {
        const slotId =
          slotIdByDate.get(localDate) ??
          operatorSlots.find(
            (slot) =>
              formatUtcInViewerTimezone(slot.utcDateTime, 'yyyy-MM-dd') === localDate &&
              formatUtcInViewerTimezone(slot.utcDateTime, 'h:mm a') === dayDrawerStartTime &&
              slot.type === dayDrawerType,
          )?.id;
        if (!slotId) continue;
        await createEvent.mutateAsync({
          slotId,
          clientUserUid: primaryClientUser?.uid || undefined,
          attendees: additionalAttendees.length ? additionalAttendees : undefined,
          title: dayDrawerEventTitle.trim() || undefined,
          description: dayDrawerEventDescription.trim() || undefined,
          durationMinutes: dayDrawerDurationMinutes,
        });
      }
      closeDayDrawer();
    } catch {
      // keep drawer open for correction
    }
  };

  const handleAddAvailabilityOverride = useCallback(async () => {
    if (
      !dayDrawerCanManageAvailability ||
      !dayDrawerFirstDate ||
      !dayDrawerAvailabilityType ||
      !availabilityTypes.includes(dayDrawerAvailabilityType)
    ) {
      return;
    }
    try {
      await createSlots.mutateAsync([
        {
          localDate: dayDrawerFirstDate,
          localTime: dayDrawerAvailabilityTime,
          timezone,
          type: dayDrawerAvailabilityType,
          isActive: true,
          isRecurring: false,
        },
      ]);
    } catch {
      // keep drawer state for correction
    }
  }, [
    createSlots,
    dayDrawerAvailabilityTime,
    dayDrawerAvailabilityType,
    dayDrawerCanManageAvailability,
    dayDrawerFirstDate,
    availabilityTypes,
    timezone,
  ]);

  const handleRemoveAvailabilitySlot = useCallback(
    async (slotId: string) => {
      try {
        await deleteSlot.mutateAsync(slotId);
      } catch {
        // leave current view intact
      }
    },
    [deleteSlot],
  );

  const handleClearAvailabilityForDay = useCallback(async () => {
    if (!dayDrawerCanManageAvailability) return;
    const targetDay = dayDrawerAvailabilitySlotsByDate[0];
    const removableSlotIds = (targetDay?.slots ?? [])
      .filter((slot) => !bookedSlotIds.has(slot.id))
      .map((slot) => slot.id);
    if (!removableSlotIds.length) return;
    try {
      await Promise.all(removableSlotIds.map((slotId) => deleteSlot.mutateAsync(slotId)));
    } catch {
      // leave current view intact
    }
  }, [bookedSlotIds, dayDrawerAvailabilitySlotsByDate, dayDrawerCanManageAvailability, deleteSlot]);

  useEffect(() => {
    if (!dayDrawerOpen && !settingsModalOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (dayDrawerOpen) closeDayDrawer();
      if (settingsModalOpen) setSettingsModalOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [dayDrawerOpen, settingsModalOpen]);

  useEffect(() => {
    if (!dayDrawerOpen || !dayDrawerTypeMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (dayDrawerTypeMenuRef.current?.contains(target)) return;
      setDayDrawerTypeMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [dayDrawerOpen, dayDrawerTypeMenuOpen]);

  useEffect(() => {
    if (!dayDrawerOpen || !dayDrawerUserPickerOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (dayDrawerUserPickerRef.current?.contains(target)) return;
      setDayDrawerUserPickerOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [dayDrawerOpen, dayDrawerUserPickerOpen]);

  useEffect(() => {
    if (!isDaySelectionDragging) return;
    const onPointerUp = () => {
      finishDaySelection();
    };
    window.addEventListener('pointerup', onPointerUp);
    return () => window.removeEventListener('pointerup', onPointerUp);
  }, [finishDaySelection, isDaySelectionDragging]);

  useEffect(() => {
    setCalendarSelection([]);
    calendarSelectionRef.current = [];
    isDaySelectionDraggingRef.current = false;
    setIsDaySelectionDragging(false);
    dragVisitedDayKeysRef.current.clear();
  }, [month]);

  const handleCreateEvent = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSlotId) return;
    const cleanedAttendees = attendees
      .map((attendee) => ({
        name: attendee.name.trim(),
        email: attendee.email.trim(),
      }))
      .filter((attendee) => attendee.name || attendee.email);

    createEvent.mutate(
      {
        slotId: selectedSlotId,
        title: eventTitle.trim() || undefined,
        description: eventDescription.trim() || undefined,
        attendees: cleanedAttendees.map((attendee) => ({
          name: attendee.name || undefined,
          email: attendee.email || undefined,
          role: 'client_team',
        })),
      },
      {
        onSuccess: () => {
          setSelectedSlotId('');
          setEventTitle('');
          setEventDescription('');
          setAttendees([]);
        },
      },
    );
  };

  const days = useMemo(() => buildCalendarDays(month), [month]);
  const isOperatorLoading =
    isOperator &&
    (meetingTypesQuery.isLoading || settingsQuery.isLoading || slotsQuery.isLoading || rulesQuery.isLoading);
  const isClientLoading = isClient && bookableQuery.isLoading;
  const dayDrawerBusy =
    createSlots.isPending || createEvent.isPending || deleteSlot.isPending;
  const settingsBusy =
    settingsSaving ||
    updateScheduleSettings.isPending ||
    createRule.isPending ||
    deleteRule.isPending;

  return (
    <Page>
      {isOperator ? (
        <OperatorTop>
          <Header>
            <Title>Schedule</Title>
            <Subtitle>
              Set weekly availability in settings, drag to select days, and create events.
            </Subtitle>
          </Header>
          <EventsRailHeader>
            <EventsRailHeading>Today&apos;s schedule</EventsRailHeading>
            <EventsRailSubtext>Your scheduled events for today.</EventsRailSubtext>
            <EventsRailSubtext>Timezone: {timezone}</EventsRailSubtext>
          </EventsRailHeader>
        </OperatorTop>
      ) : (
        <Header>
          <Title>Schedule</Title>
          <Subtitle>
            Book events with available agents and admins based on published availability.
          </Subtitle>
        </Header>
      )}

      {isOperator ? (
        <OperatorLayout>
          <CalendarPanel>
            <CalendarHeader>
              <CalendarNavGroup>
                <NavButton
                  type="button"
                  aria-label="Previous month"
                  onClick={() => setMonth((prev) => subMonths(prev, 1))}
                >
                  {'<'}
                </NavButton>
                <MonthLabel>{format(month, 'MMMM yyyy')}</MonthLabel>
                <NavButton
                  type="button"
                  aria-label="Next month"
                  onClick={() => setMonth((prev) => addMonths(prev, 1))}
                >
                  {'>'}
                </NavButton>
              </CalendarNavGroup>
              <CalendarSettingsButton
                type="button"
                onClick={() => setSettingsModalOpen(true)}
              >
                Availability settings
              </CalendarSettingsButton>
            </CalendarHeader>

            {isOperatorLoading ? <Helper>Loading schedule...</Helper> : null}

            <CalendarGrid>
              {DAY_HEADERS.map((header) => (
                <DayHeader key={header}>{header}</DayHeader>
              ))}
              {days.map((day) => {
                const out = !isSameMonth(day, month);
                const dayStart = startOfDay(day);
                const todayStart = startOfDay(new Date());
                const isTodayDate = isSameDay(dayStart, todayStart);
                const futureInMonth = !out && !isTodayDate && dayStart > todayStart;
                const localDate = format(day, 'yyyy-MM-dd');
                const isSelected = calendarSelection.includes(localDate);
                const dayEventTypes = scheduledEventTypesByDay.get(localDate) ?? [];
                const typeSet = Array.from(new Set(dayEventTypes));
                return (
                  <DayCell
                    key={day.toISOString()}
                    $out={out}
                    $today={isTodayDate}
                    $futureInMonth={futureInMonth}
                    $selected={isSelected}
                    $disabled={out}
                    onPointerDown={(event) => {
                      if (out) return;
                      if (event.button !== 0) return;
                      event.preventDefault();
                      startDaySelection(day);
                    }}
                    onPointerEnter={() => {
                      if (out) return;
                      continueDaySelection(day);
                    }}
                    onPointerUp={() => {
                      if (out) return;
                      finishDaySelection();
                    }}
                    onKeyDown={(event) => {
                      if (out) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setCalendarSelectionState(() => [localDate]);
                      openDayEditorForSelection([localDate]);
                    }}
                  >
                    <DayNumber
                      $out={out}
                      $today={isTodayDate}
                      $futureInMonth={futureInMonth}
                    >
                      {day.getDate()}
                    </DayNumber>
                    <DotRow>
                      {typeSet.slice(0, 4).map((type) => (
                        <Dot key={`${localDate}-${type}`} $type={type} />
                      ))}
                    </DotRow>
                  </DayCell>
                );
              })}
            </CalendarGrid>
          </CalendarPanel>

          <EventsRailWrap>
            <EventsRailPanel>
              <EventsRailListShell>
                <EventsRailList>
                  {todaysEvents.length ? (
                    todaysEvents.map((event) => (
                      <EventsRailCard key={event.id}>
                        <EventsRailTitle>{event.title}</EventsRailTitle>
                        <EventsRailMeta>
                          {formatUtcInViewerTimezone(event.startsAt, 'MMM d, yyyy h:mm a')}
                        </EventsRailMeta>
                        <EventsRailMeta>
                          {SCHEDULE_MEETING_TYPE_LABELS[event.meetingType]}
                        </EventsRailMeta>
                      </EventsRailCard>
                    ))
                  ) : (
                    <EventsRailCard>
                      <EventsRailTitle>No events scheduled</EventsRailTitle>
                    </EventsRailCard>
                  )}
                </EventsRailList>
              </EventsRailListShell>
            </EventsRailPanel>
          </EventsRailWrap>
        </OperatorLayout>
      ) : isClient ? (
        <Layout>
          <Panel as="form" onSubmit={handleCreateEvent}>
            <PanelTitle>Book an event</PanelTitle>
            <Text>
              Select an available slot, add event context, and include team members who should join.
            </Text>
            <Field>
              Selected slot
              <Input
                value={
                  selectedSlot
                    ? `${formatUtcInViewerTimezone(
                        selectedSlot.utcDateTime,
                        'yyyy-MM-dd',
                      )} ${formatUtcInViewerTimezone(
                        selectedSlot.utcDateTime,
                        'h:mm a',
                      )} (${SCHEDULE_MEETING_TYPE_LABELS[selectedSlot.type]})`
                    : 'No slot selected'
                }
                readOnly
              />
            </Field>
            <Field>
              Event title (optional)
              <Input
                value={eventTitle}
                onChange={(event) => setEventTitle(event.target.value)}
                placeholder="Example: Weekly sync"
              />
            </Field>
            <Field>
              Description (optional)
              <Textarea
                value={eventDescription}
                onChange={(event) => setEventDescription(event.target.value)}
                placeholder="Agenda, goals, and prep notes."
              />
            </Field>
            <PanelTitle>Team members (optional)</PanelTitle>
            {attendees.map((attendee, index) => (
              <FieldGrid key={`attendee-${index}`}>
                <Field>
                  Name
                  <Input
                    value={attendee.name}
                    onChange={(event) => {
                      const next = [...attendees];
                      next[index] = { ...next[index], name: event.target.value };
                      setAttendees(next);
                    }}
                  />
                </Field>
                <Field>
                  Email
                  <Input
                    value={attendee.email}
                    onChange={(event) => {
                      const next = [...attendees];
                      next[index] = { ...next[index], email: event.target.value };
                      setAttendees(next);
                    }}
                  />
                </Field>
              </FieldGrid>
            ))}
            <Row>
              <Button
                type="button"
                $ghost
                onClick={() => setAttendees((prev) => [...prev, { name: '', email: '' }])}
              >
                Add team member
              </Button>
              {attendees.length ? (
                <Button
                  type="button"
                  $ghost
                  onClick={() => setAttendees((prev) => prev.slice(0, -1))}
                >
                  Remove last
                </Button>
              ) : null}
            </Row>
            <Button type="submit" disabled={!selectedSlotId || createEvent.isPending}>
              {createEvent.isPending ? 'Scheduling...' : 'Schedule event'}
            </Button>
          </Panel>

          <Panel>
            <PanelTitle>Available slots this month</PanelTitle>
            {isClientLoading ? <Helper>Loading available slots...</Helper> : null}
            {groupedBookable.length ? (
              groupedBookable.map((group) => (
                <SlotGroup key={group.date}>
                  <SlotDateLabel>
                    {format(parseISO(`${group.date}T00:00:00`), 'EEEE, MMM d')}
                  </SlotDateLabel>
                  <SlotButtons>
                    {group.slots.map((slot) => (
                      <SlotButton
                        key={slot.id}
                        type="button"
                        $active={selectedSlotId === slot.id}
                        $type={slot.type}
                        onClick={() => setSelectedSlotId(slot.id)}
                      >
                        {formatUtcInViewerTimezone(slot.utcDateTime, 'h:mm a')} |{' '}
                        {SCHEDULE_MEETING_TYPE_LABELS[slot.type]}
                      </SlotButton>
                    ))}
                  </SlotButtons>
                </SlotGroup>
              ))
            ) : (
              <Helper>No available slots published for this month.</Helper>
            )}

            <Divider />
            <PanelTitle>Your upcoming events</PanelTitle>
            {upcomingEvents.length ? (
              upcomingEvents.map((event) => (
                <Item key={event.id}>
                  <ItemTitle>{event.title}</ItemTitle>
                  <ItemMeta>
                    {formatUtcInViewerTimezone(event.startsAt, 'MMM d, yyyy h:mm a')} |{' '}
                    {SCHEDULE_MEETING_TYPE_LABELS[event.meetingType]}
                  </ItemMeta>
                </Item>
              ))
            ) : (
              <Helper>No upcoming events scheduled.</Helper>
            )}
          </Panel>
        </Layout>
      ) : (
        <Panel>
          <PanelTitle>Schedule is unavailable</PanelTitle>
          <Text>Your role does not currently have schedule access.</Text>
        </Panel>
      )}

      <AnimatePresence>
        {dayDrawerOpen && dayDrawerDate && (
          <>
            <DayDrawerBackdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDayDrawer}
            />
            <DayDrawerSheet
              role="dialog"
              aria-modal="true"
              aria-label="Create calendar event"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            >
              <DayDrawerHeader>
                <DayDrawerTitleWrap>
                  <DayDrawerTitle>
                    {dayDrawerDates.length > 1 ? 'Plan selected days' : 'Plan selected day'}
                  </DayDrawerTitle>
                  <DayDrawerDate>
                    {dayDrawerDates.length > 1
                      ? `${dayDrawerDates.length} days selected`
                      : format(dayDrawerDate, 'EEEE, MMMM d, yyyy')}
                  </DayDrawerDate>
                </DayDrawerTitleWrap>
                <DayDrawerClose onClick={closeDayDrawer} aria-label="Close drawer">
                  x
                </DayDrawerClose>
              </DayDrawerHeader>

              <DayDrawerTabs>
                <DayDrawerTab
                  type="button"
                  $active={dayDrawerTab === 'event'}
                  onClick={() => setDayDrawerTab('event')}
                >
                  Event
                </DayDrawerTab>
                <DayDrawerTab
                  type="button"
                  $active={dayDrawerTab === 'availability'}
                  onClick={() => setDayDrawerTab('availability')}
                >
                  Availability
                </DayDrawerTab>
              </DayDrawerTabs>

              <DayDrawerBody>
                <DayDrawerSection>
                  <DayDrawerSectionBox>
                    {dayDrawerTab === 'event' ? (
                      <>
                    <DayDrawerSectionTitle>Scheduled events</DayDrawerSectionTitle>
                    {dayDrawerScheduledEvents.length ? (
                      <DayDrawerEventList>
                        {dayDrawerScheduledEvents.map((event) => (
                          <DayDrawerEventCard key={event.id}>
                            <DayDrawerEventTitle>{event.title}</DayDrawerEventTitle>
                            <DayDrawerEventMeta>
                              {dayDrawerDates.length > 1
                                ? `${formatUtcInViewerTimezone(event.startsAt, 'EEE, MMM d')} | `
                                : ''}
                              {formatUtcInViewerTimezone(event.startsAt, 'h:mm a')} |{' '}
                              {SCHEDULE_MEETING_TYPE_LABELS[event.meetingType]}
                            </DayDrawerEventMeta>
                            <DayDrawerEventMeta>
                              {event.clientUserId
                                ? 'Client meeting'
                                : event.attendees.length
                                  ? `${event.attendees.length} attendee${event.attendees.length === 1 ? '' : 's'}`
                                  : 'Internal event'}
                            </DayDrawerEventMeta>
                          </DayDrawerEventCard>
                        ))}
                      </DayDrawerEventList>
                    ) : (
                      <DayDrawerEmptyState>
                        No meetings are scheduled for this {dayDrawerDates.length > 1 ? 'selection' : 'day'}.
                      </DayDrawerEmptyState>
                    )}
                    <DayDrawerButton
                      type="button"
                      onClick={() =>
                        setDayDrawerShowCreateForm((current) => !current)
                      }
                    >
                      {dayDrawerShowCreateForm ? 'Hide create event form' : 'Create event'}
                    </DayDrawerButton>
                    {dayDrawerShowCreateForm ? (
                      <>
                    <DayDrawerSectionTitle>Event details</DayDrawerSectionTitle>
                    <DayDrawerField>
                      Event title (optional)
                      <DayDrawerInput
                        value={dayDrawerEventTitle}
                        onChange={(event) => setDayDrawerEventTitle(event.target.value)}
                        placeholder="Example: Weekly sync"
                      />
                    </DayDrawerField>
                    <DayDrawerField>
                      Description (optional)
                      <DayDrawerTextarea
                        value={dayDrawerEventDescription}
                        onChange={(event) =>
                          setDayDrawerEventDescription(event.target.value)
                        }
                        placeholder="Agenda or notes..."
                      />
                    </DayDrawerField>
                    <DayDrawerTypeRow>
                      <DayDrawerMeetingTypePicker ref={dayDrawerTypeMenuRef}>
                        <DayDrawerMeetingTypeButton
                          type="button"
                          $open={dayDrawerTypeMenuOpen}
                          aria-haspopup="listbox"
                          aria-expanded={dayDrawerTypeMenuOpen}
                          aria-label="Meeting type"
                          disabled={!allowedTypes.length}
                          onClick={() =>
                            setDayDrawerTypeMenuOpen((open) => {
                              if (!allowedTypes.length) return false;
                              return !open;
                            })
                          }
                        >
                          <DayDrawerMeetingTypeButtonLabel>
                            {dayDrawerType
                              ? SCHEDULE_MEETING_TYPE_LABELS[dayDrawerType]
                              : 'No meeting permissions'}
                          </DayDrawerMeetingTypeButtonLabel>
                          <DayDrawerMeetingTypeCaret $open={dayDrawerTypeMenuOpen}>
                            v
                          </DayDrawerMeetingTypeCaret>
                        </DayDrawerMeetingTypeButton>
                        {dayDrawerTypeMenuOpen && allowedTypes.length ? (
                          <DayDrawerMeetingTypeMenu role="listbox" aria-label="Meeting types">
                            {allowedTypes.map((type) => (
                              <DayDrawerMeetingTypeOption
                                key={type}
                                type="button"
                                role="option"
                                aria-selected={dayDrawerType === type}
                                $selected={dayDrawerType === type}
                                onClick={() => {
                                  setDayDrawerType(type);
                                  setDayDrawerTypeMenuOpen(false);
                                }}
                              >
                                {SCHEDULE_MEETING_TYPE_LABELS[type]}
                              </DayDrawerMeetingTypeOption>
                            ))}
                          </DayDrawerMeetingTypeMenu>
                        ) : null}
                      </DayDrawerMeetingTypePicker>
                    </DayDrawerTypeRow>
                    <DayDrawerSectionTitle>Guests and timing</DayDrawerSectionTitle>
                    <DayDrawerUsersRow>
                      <DayDrawerUsersTitle>add users</DayDrawerUsersTitle>
                      <DayDrawerUserPicker ref={dayDrawerUserPickerRef}>
                        <DayDrawerSelectedUsersList>
                          {selectedOperatorUsers.map((user) => {
                            const photoURL =
                              user.photoURL ?? dayDrawerUserPhotoByUid[user.uid] ?? null;
                            return (
                              <DayDrawerSelectedUserAvatar
                                key={user.uid}
                                title={user.label}
                                aria-label={user.label}
                              >
                                <PortalAvatar
                                  uid={user.uid}
                                  email={user.email}
                                  name={user.label}
                                  photoURL={photoURL}
                                  size={40}
                                />
                                <DayDrawerSelectedUserRemove
                                  type="button"
                                  title={`Remove ${user.label}`}
                                  aria-label={`Remove ${user.label}`}
                                  onClick={() =>
                                    setDayDrawerSelectedUserUids((previous) =>
                                      previous.filter((uid) => uid !== user.uid),
                                    )
                                  }
                                >
                                  x
                                </DayDrawerSelectedUserRemove>
                              </DayDrawerSelectedUserAvatar>
                            );
                          })}
                          <DayDrawerAddUserButton
                            type="button"
                            $active={dayDrawerUserPickerOpen}
                            aria-label="Add users"
                            onClick={() => setDayDrawerUserPickerOpen((open) => !open)}
                          >
                            +
                          </DayDrawerAddUserButton>
                        </DayDrawerSelectedUsersList>

                        {dayDrawerUserPickerOpen ? (
                          <DayDrawerUserMenu>
                            <DayDrawerUserSearch
                              value={dayDrawerUserSearch}
                              onChange={(event) => setDayDrawerUserSearch(event.target.value)}
                              placeholder="Search users by name or business..."
                            />
                            <DayDrawerUserList>
                              {usersQuery.isLoading ? (
                                <DayDrawerUserEmpty>Loading users...</DayDrawerUserEmpty>
                              ) : usersQuery.isError ? (
                                <DayDrawerUserEmpty>
                                  Could not load users. Check permissions.
                                </DayDrawerUserEmpty>
                              ) : filteredOperatorUsers.length ? (
                                filteredOperatorUsers.map((user) => (
                                  <DayDrawerUserItem
                                    key={user.uid}
                                    type="button"
                                    $selected={dayDrawerSelectedUserUids.includes(user.uid)}
                                    onClick={() => {
                                      setDayDrawerSelectedUserUids((previous) => {
                                        if (previous.includes(user.uid)) return previous;
                                        return [...previous, user.uid];
                                      });
                                      setDayDrawerUserPickerOpen(false);
                                      setDayDrawerUserSearch('');
                                    }}
                                  >
                                    <DayDrawerUserName>{user.label}</DayDrawerUserName>
                                    <DayDrawerUserMeta>
                                      {user.businessName ?? user.name ?? '--'} | {user.email}
                                    </DayDrawerUserMeta>
                                  </DayDrawerUserItem>
                                ))
                              ) : (
                                <DayDrawerUserEmpty>
                                  {dayDrawerUserSearch.trim().length
                                    ? 'No users match this search.'
                                    : 'No users found.'}
                                </DayDrawerUserEmpty>
                              )}
                            </DayDrawerUserList>
                          </DayDrawerUserMenu>
                        ) : null}
                      </DayDrawerUserPicker>
                    </DayDrawerUsersRow>
                    {dayDrawerSelectedUserUids.length ? (
                      <DayDrawerScheduleHint>
                        {dayDrawerSelectedUserUids.length} user
                        {dayDrawerSelectedUserUids.length === 1 ? '' : 's'} will receive this
                        event.
                      </DayDrawerScheduleHint>
                    ) : null}
                    <DayDrawerInlineGrid>
                      <DayDrawerField>
                        Start time
                        <DayDrawerTypeSelect
                          value={dayDrawerStartTime}
                          onChange={(event) => setDayDrawerStartTime(event.target.value)}
                        >
                          {allTimeSlots.map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ))}
                        </DayDrawerTypeSelect>
                      </DayDrawerField>
                      <DayDrawerField>
                        Duration
                        <DayDrawerTypeSelect
                          value={String(dayDrawerDurationMinutes)}
                          onChange={(event) =>
                            setDayDrawerDurationMinutes(Number(event.target.value))
                          }
                        >
                          {[15, 30, 45, 60, 90, 120].map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {minutes} min
                            </option>
                          ))}
                        </DayDrawerTypeSelect>
                      </DayDrawerField>
                    </DayDrawerInlineGrid>
                      </>
                    ) : null}
                      </>
                    ) : (
                      <>
                        {!dayDrawerCanManageAvailability ? (
                          <DayDrawerEmptyState>
                            Availability overrides are available when a single day is selected.
                          </DayDrawerEmptyState>
                        ) : (
                          <>
                            <DayDrawerSectionTitle>Published availability</DayDrawerSectionTitle>
                            {dayDrawerAvailabilityWindowsByDate[0]?.windows.length ? (
                              <DayDrawerEventList>
                                {dayDrawerAvailabilityWindowsByDate[0].windows.map((window) => (
                                  <DayDrawerAvailabilityCard
                                    key={`${dayDrawerAvailabilitySlotsByDate[0]?.dateKey ?? 'day'}-${window.startTime}-${window.endTime}`}
                                  >
                                    <DayDrawerAvailabilityCopy>
                                      <DayDrawerAvailabilityTitle>
                                        {window.startTime} - {window.endTime}
                                      </DayDrawerAvailabilityTitle>
                                      <DayDrawerAvailabilityMeta>
                                        {window.hasRecurring && window.hasOneOff
                                          ? 'Recurring and one-off availability'
                                          : window.hasOneOff
                                          ? 'One-off availability'
                                          : 'Recurring availability'}
                                      </DayDrawerAvailabilityMeta>
                                    </DayDrawerAvailabilityCopy>
                                  </DayDrawerAvailabilityCard>
                                ))}
                              </DayDrawerEventList>
                            ) : (
                              <DayDrawerEmptyState>
                                No availability is published for this day.
                              </DayDrawerEmptyState>
                            )}
                            <DayDrawerSectionTitle>Override this day</DayDrawerSectionTitle>
                            <DayDrawerInlineGrid>
                              <DayDrawerField>
                                Meeting type
                                <DayDrawerTypeSelect
                                  value={dayDrawerAvailabilityType ?? ''}
                                  onChange={(event) =>
                                    setDayDrawerAvailabilityType(
                                      (event.target.value || null) as ScheduleMeetingType | null,
                                    )
                                  }
                                >
                                  <option value="">Select type</option>
                                  {availabilityTypes.map((type) => (
                                    <option key={`availability-${type}`} value={type}>
                                      {SCHEDULE_MEETING_TYPE_LABELS[type]}
                                    </option>
                                  ))}
                                </DayDrawerTypeSelect>
                              </DayDrawerField>
                              <DayDrawerField>
                                Start time
                                <DayDrawerTypeSelect
                                  value={dayDrawerAvailabilityTime}
                                  onChange={(event) =>
                                    setDayDrawerAvailabilityTime(event.target.value)
                                  }
                                >
                                  {allTimeSlots.map((slot) => (
                                    <option key={`availability-time-${slot}`} value={slot}>
                                      {slot}
                                    </option>
                                  ))}
                                </DayDrawerTypeSelect>
                              </DayDrawerField>
                            </DayDrawerInlineGrid>
                            <DayDrawerAvailabilityActions>
                              <DayDrawerInlineAction
                                type="button"
                                disabled={
                                  dayDrawerBusy ||
                                  !dayDrawerAvailabilityType ||
                                  !availabilityTypes.length
                                }
                                onClick={handleAddAvailabilityOverride}
                              >
                                Add availability
                              </DayDrawerInlineAction>
                              <DayDrawerInlineAction
                                type="button"
                                $danger
                                disabled={
                                  dayDrawerBusy ||
                                  !dayDrawerAvailabilitySlotsByDate[0]?.slots.some(
                                    (slot) => !bookedSlotIds.has(slot.id),
                                  )
                                }
                                onClick={handleClearAvailabilityForDay}
                              >
                                Clear day
                              </DayDrawerInlineAction>
                            </DayDrawerAvailabilityActions>
                            <DayDrawerScheduleHint>
                              Remove slots to block time on this day, or add one-off slots to override your regular availability.
                            </DayDrawerScheduleHint>
                          </>
                        )}
                      </>
                    )}
                  </DayDrawerSectionBox>
                </DayDrawerSection>

              </DayDrawerBody>

              {dayDrawerShowCreateForm ? (
                <DayDrawerActionBar>
                  <DayDrawerButton
                    type="button"
                    $primary
                    $block
                    disabled={
                      !dayDrawerType ||
                      dayDrawerBusy ||
                      !allowedTypes.length ||
                      dayDrawerRepeatRangeInvalid ||
                      !dayDrawerExpandedDates.length
                    }
                    onClick={handleCreateEventFromDayDrawer}
                  >
                    {dayDrawerBusy && createEvent.isPending
                      ? 'Scheduling...'
                      : dayDrawerExpandedDates.length > 1
                      ? `Create ${dayDrawerExpandedDates.length} events`
                      : 'Create event'}
                  </DayDrawerButton>
                </DayDrawerActionBar>
              ) : null}
            </DayDrawerSheet>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOperator && settingsModalOpen ? (
          <>
            <SettingsBackdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSettingsModalOpen(false)}
            />
            <SettingsModal
              role="dialog"
              aria-modal="true"
              aria-label="Availability settings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              onClick={() => setSettingsModalOpen(false)}
            >
              <SettingsCard onClick={(event) => event.stopPropagation()}>
                <SettingsHeader>
                  <SettingsTitleWrap>
                    <SettingsTitle>Availability settings</SettingsTitle>
                    <SettingsSubtitle>
                      Configure availability windows by day for client booking.
                    </SettingsSubtitle>
                  </SettingsTitleWrap>
                  <SettingsCloseButton
                    type="button"
                    onClick={() => setSettingsModalOpen(false)}
                    aria-label="Close availability settings"
                  >
                    x
                  </SettingsCloseButton>
                </SettingsHeader>

                <SettingsBody>
                  <SettingsSection>
                    <SettingsSectionTitle>Day hours</SettingsSectionTitle>
                    <SettingsSubtitle>
                      Each day can have its own hours. Disabled days are unavailable.
                    </SettingsSubtitle>
                    <SettingsDayRows>
                      {settingsDayRows.map(({ label, dayIndex, config }) => (
                        <SettingsDayRow key={label} $enabled={config.enabled}>
                          <SettingsDayLabel>{label}</SettingsDayLabel>
                          <SettingsField>
                            Start time
                            <SettingsSelect
                              value={config.startTime}
                              disabled={!config.enabled}
                              onChange={(event) =>
                                updateSettingsDay(dayIndex, {
                                  startTime: event.target.value,
                                })
                              }
                            >
                              {allTimeSlots.map((slot) => (
                                <option key={`${label}-start-${slot}`} value={slot}>
                                  {slot}
                                </option>
                              ))}
                            </SettingsSelect>
                          </SettingsField>
                          <SettingsField>
                            End time
                            <SettingsSelect
                              value={config.endTime}
                              disabled={!config.enabled}
                              onChange={(event) =>
                                updateSettingsDay(dayIndex, {
                                  endTime: event.target.value,
                                })
                              }
                            >
                              {allTimeSlots.map((slot) => (
                                <option key={`${label}-end-${slot}`} value={slot}>
                                  {slot}
                                </option>
                              ))}
                            </SettingsSelect>
                          </SettingsField>
                          <SettingsDayToggleWrap>
                            <SettingsDayToggleText>
                              {config.enabled ? 'On' : 'Off'}
                            </SettingsDayToggleText>
                            <SettingsSwitch
                              type="button"
                              role="switch"
                              aria-checked={config.enabled}
                              aria-label={`${label} availability`}
                              $active={config.enabled}
                              onClick={() =>
                                updateSettingsDay(dayIndex, {
                                  enabled: !config.enabled,
                                })
                              }
                            >
                              <SettingsSwitchThumb $active={config.enabled} />
                            </SettingsSwitch>
                          </SettingsDayToggleWrap>
                        </SettingsDayRow>
                      ))}
                    </SettingsDayRows>
                    {settingsHasInvalidDays ? (
                      <SettingsSubtitle $error>
                        Enabled days must have an end time later than start time.
                      </SettingsSubtitle>
                    ) : null}
                  </SettingsSection>

                  <SettingsSection>
                    <SettingsSectionTitle>Calendar status</SettingsSectionTitle>
                    <SettingsToggleRow>
                      <SettingsToggleCopy>
                        <SettingsToggleLabel>
                          Suspend calendar (unavailable for bookings)
                        </SettingsToggleLabel>
                        <SettingsToggleHint>
                          Toggle off when you are ready to accept bookings.
                        </SettingsToggleHint>
                      </SettingsToggleCopy>
                      <SettingsSwitch
                        type="button"
                        role="switch"
                        aria-checked={settingsSuspendCalendar}
                        aria-label="Suspend calendar"
                        $active={settingsSuspendCalendar}
                        onClick={() =>
                          setSettingsSuspendCalendar((current) => !current)
                        }
                      >
                        <SettingsSwitchThumb $active={settingsSuspendCalendar} />
                      </SettingsSwitch>
                    </SettingsToggleRow>
                  </SettingsSection>
                </SettingsBody>

                <SettingsFooter>
                  <SettingsFooterButton
                    type="button"
                    onClick={() => setSettingsModalOpen(false)}
                  >
                    Cancel
                  </SettingsFooterButton>
                  <SettingsFooterButton
                    type="button"
                    $accent
                    disabled={
                      settingsBusy ||
                      (!settingsSuspendCalendar &&
                        (!settingsCanSave || settingsHasInvalidDays))
                    }
                    onClick={handleSaveAvailabilitySettings}
                  >
                    {settingsBusy ? 'Saving...' : 'Save'}
                  </SettingsFooterButton>
                </SettingsFooter>
              </SettingsCard>
            </SettingsModal>
          </>
        ) : null}
      </AnimatePresence>
    </Page>
  );
}
